// VenueDashboardMenuPage.jsx — EV-759: owner DIGITAL-MENU editor.
// Was a "coming soon" placeholder — now the real editor on the CURRENT menu
// schema (ai.venue_google_menus, the LIVE consumer table getVenueMenu reads).
// Owner/manager/staff can add / edit / delete items: Vietnamese name (primary),
// English name, price (VND), description, vegetarian flag, and hide/show on the
// public menu. Photos: shows the existing item image; full gallery picker is a
// follow-up (EV-765).
//
// 🔴 SCHEMA NOTE: this is wired to ai.venue_google_menus AS-IS. When the menu
// schema migration (EV-529) lands, this page + the consumer MenuPage/RichMenuView
// + the agent menu editor + the offers item-scope picker ALL need re-wiring.
//
// API (member-gated, Neon): GET /api/venue-dashboard/:slug (venue id + role) ·
// GET/POST /api/me/venues/:id/menu-items · PATCH/DELETE …/menu-items/:itemId

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';

const RED = '#ef4444';
const ORANGE = '#E46E34';

function token() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') || ''
  );
}
const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

const fmtVnd = (p) => {
  const n = Number(p);
  if (!p || isNaN(n)) return '';
  return `${n.toLocaleString()}₫`;
};

// Pull the English name out of the translations blob (item_name is vi-first).
const enName = (it) => (it.translations && it.translations.en && it.translations.en.name) || '';

export default function VenueDashboardMenuPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { t } = useTranslation();
  const base = getApiBaseUrl();

  const [venue, setVenue] = useState(null);   // {id, role}
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // item object | 'new' | null
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const vRes = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, { headers: authHeaders() });
      const vj = await vRes.json();
      const v = vj?.venue || vj?.data || vj;
      if (!v?.id) throw new Error(vj?.message || t('vd.venueNotFoundNoAccess', 'Venue not found / no access'));
      const role = vj?.myRole || v.myRole || v.role || null;
      setVenue({ id: v.id, role });
      const mRes = await fetch(`${base}/me/venues/${v.id}/menu-items`, { headers: authHeaders() });
      const mj = await mRes.json();
      if (!mj?.success) throw new Error(mj?.error || mj?.message || t('vd.couldNotLoadMenu', 'Could not load menu'));
      setItems(mj.items || []);
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [base, slug, t]);

  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    if (!venue) return;
    setBusy(true);
    try {
      const body = {
        name: form.name,                 // Vietnamese / primary
        name_en: form.name_en || undefined,
        section: form.section || 'Menu',
        description: form.description || undefined,
        price_vnd: form.price_vnd === '' ? null : form.price_vnd,
        is_vegetarian: !!form.is_vegetarian,
        image_url: form.image_url !== undefined ? (form.image_url || null) : undefined,
        menu_status: form.menu_status || 'active',
      };
      let res;
      if (form.id) {
        res = await fetch(`${base}/me/venues/${venue.id}/menu-items/${form.id}`, {
          method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${base}/me/venues/${venue.id}/menu-items`, {
          method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
        });
      }
      const j = await res.json();
      if (!j?.success) throw new Error(j?.error || j?.message || t('vd.saveFailed', 'Save failed'));
      setItems((prev) => {
        if (form.id) return prev.map((x) => (x.id === j.item.id ? { ...x, ...j.item } : x));
        return [...prev, j.item];
      });
      setEditing(null);
    } catch (e) { window.alert(e.message); }
    setBusy(false);
  };

  const remove = async (it) => {
    if (!venue) return;
    if (!window.confirm(`${t('vd.deleteItemConfirm', 'Delete this item?')}\n\n${it.item_name}`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${base}/me/venues/${venue.id}/menu-items/${it.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const j = await res.json();
      if (!j?.success) throw new Error(j?.error || t('vd.deleteFailed', 'Delete failed'));
      setItems((prev) => prev.filter((x) => x.id !== it.id));
    } catch (e) { window.alert(e.message); }
    setBusy(false);
  };

  const toggleHidden = async (it) => {
    await save({
      id: it.id, name: it.item_name, name_en: enName(it), section: it.section_name,
      description: it.item_description, price_vnd: it.item_price ?? '',
      is_vegetarian: it.is_vegetarian, image_url: it.item_image_url,
      menu_status: it.menu_status === 'parked' ? 'active' : 'parked',
    });
  };

  // group by section, active first
  const active = items.filter((i) => i.menu_status !== 'parked');
  const parked = items.filter((i) => i.menu_status === 'parked');
  const sections = [];
  active.forEach((i) => {
    const s = i.section_name || 'Menu';
    let g = sections.find((x) => x.name === s);
    if (!g) { g = { name: s, items: [] }; sections.push(g); }
    g.items.push(i);
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', position: 'sticky', top: 0, zIndex: 100,
        background: `linear-gradient(135deg, ${RED} 0%, #f97316 100%)`,
      }}>
        <button onClick={() => navigate(`/venue-dashboard/${slug}`)} style={{
          background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8,
          color: 'white', fontSize: 20, width: 36, height: 36, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>←</button>
        <span style={{ color: 'white', fontWeight: 700, fontSize: 17 }}>{t('vd.menuPrices', 'Menu & Prices')}</span>
        <div style={{ width: 36 }} />
      </div>

      <div style={{ flex: 1, maxWidth: 560, width: '100%', margin: '0 auto', padding: '14px 14px 90px' }}>
        {loading && <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>{t('common.loading', 'Loading…')}</div>}
        {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, padding: 14, fontSize: 13 }}>{err}</div>}

        {!loading && !err && (
          <>
            <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
              {t('vd.menuEditorHint', 'Edit your menu here. The Vietnamese name shows first; add an English name so tourists understand. Prices are in VND.')}
            </div>

            {editing === 'new' && (
              <ItemForm
                initial={{ section: sections[0]?.name || 'Menu' }}
                busy={busy} onCancel={() => setEditing(null)} onSave={save} t={t}
                sections={sections.map((s) => s.name)}
              />
            )}

            {editing !== 'new' && (
              <button onClick={() => setEditing('new')} style={{
                width: '100%', background: `linear-gradient(135deg, ${ORANGE}, #c2410c)`,
                border: 'none', color: 'white', borderRadius: 14, padding: '14px 16px',
                fontSize: 15.5, fontWeight: 800, cursor: 'pointer', marginBottom: 14,
                boxShadow: '0 4px 14px rgba(228,110,52,0.3)',
              }}>
                ➕ {t('vd.addMenuItem', 'Add menu item')}
              </button>
            )}

            {items.length === 0 && editing !== 'new' && (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 12px', fontSize: 14 }}>
                {t('vd.noMenuItems', 'No menu items yet — add your first dish to put your digital menu live.')}
              </div>
            )}

            {sections.map((sec) => (
              <div key={sec.name} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '4px 2px 8px' }}>{sec.name}</div>
                {sec.items.map((it) => (
                  editing && editing.id === it.id ? (
                    <ItemForm key={it.id} initial={{
                      id: it.id, section: it.section_name, name: it.item_name, name_en: enName(it),
                      price_vnd: it.item_price ?? '', description: it.item_description || '',
                      is_vegetarian: it.is_vegetarian, image_url: it.item_image_url || '', menu_status: it.menu_status,
                    }} busy={busy} onCancel={() => setEditing(null)} onSave={save} t={t}
                      sections={sections.map((s) => s.name)} />
                  ) : (
                    <ItemRow key={it.id} it={it} onEdit={() => setEditing(it)} onDelete={() => remove(it)} onToggleHidden={() => toggleHidden(it)} t={t} />
                  )
                ))}
              </div>
            ))}

            {parked.length > 0 && (
              <div style={{ marginTop: 22 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '4px 2px 8px' }}>
                  🚫 {t('vd.hiddenItems', 'Hidden from public menu')} ({parked.length})
                </div>
                {parked.map((it) => (
                  editing && editing.id === it.id ? (
                    <ItemForm key={it.id} initial={{
                      id: it.id, section: it.section_name, name: it.item_name, name_en: enName(it),
                      price_vnd: it.item_price ?? '', description: it.item_description || '',
                      is_vegetarian: it.is_vegetarian, image_url: it.item_image_url || '', menu_status: it.menu_status,
                    }} busy={busy} onCancel={() => setEditing(null)} onSave={save} t={t}
                      sections={sections.map((s) => s.name)} />
                  ) : (
                    <ItemRow key={it.id} it={it} hidden onEdit={() => setEditing(it)} onDelete={() => remove(it)} onToggleHidden={() => toggleHidden(it)} t={t} />
                  )
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <DashboardNavBar />
    </div>
  );
}

function ItemRow({ it, hidden, onEdit, onDelete, onToggleHidden, t }) {
  return (
    <div style={{
      background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: 12,
      marginBottom: 8, display: 'flex', gap: 12, alignItems: 'center',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)', opacity: hidden ? 0.6 : 1,
    }}>
      {it.item_image_url ? (
        <img src={it.item_image_url} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
      ) : (
        <div style={{ width: 48, height: 48, borderRadius: 10, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🍽️</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.item_name}</span>
          {it.is_vegetarian && <span title={t('vd.vegetarian', 'Vegetarian')} style={{ fontSize: 13, flexShrink: 0 }}>🌱</span>}
        </div>
        {(it.translations?.en?.name) && it.translations.en.name !== it.item_name && (
          <div style={{ fontSize: 12, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.translations.en.name}</div>
        )}
        <div style={{ fontSize: 13, color: '#16a34a', fontWeight: 700, marginTop: 2 }}>{fmtVnd(it.item_price) || '—'}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
        <button onClick={onEdit} style={miniBtn('#374151')}>{t('vd.edit', 'Edit')}</button>
        <button onClick={onToggleHidden} style={miniBtn(hidden ? '#16a34a' : '#b45309')}>
          {hidden ? t('vd.show', 'Show') : t('vd.hide', 'Hide')}
        </button>
        <button onClick={onDelete} style={miniBtn('#dc2626')}>{t('vd.delete', 'Delete')}</button>
      </div>
    </div>
  );
}

const miniBtn = (color) => ({
  background: 'white', border: `1px solid ${color}`, color, borderRadius: 8,
  padding: '4px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
});

function ItemForm({ initial, busy, onCancel, onSave, sections, t }) {
  const [form, setForm] = useState({
    id: initial.id, section: initial.section || 'Menu', name: initial.name || '',
    name_en: initial.name_en || '', price_vnd: initial.price_vnd ?? '',
    description: initial.description || '', is_vegetarian: !!initial.is_vegetarian,
    image_url: initial.image_url || '', menu_status: initial.menu_status || 'active',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const input = {
    width: '100%', boxSizing: 'border-box', background: 'white',
    border: '1px solid #d1d5db', borderRadius: 10, padding: '11px 12px',
    fontSize: 14, color: '#111827', outline: 'none',
  };
  const label = { fontSize: 11.5, fontWeight: 700, color: '#6b7280', margin: '12px 0 5px' };

  return (
    <div style={{ background: 'white', border: `1.5px solid ${ORANGE}55`, borderRadius: 16, padding: 16, marginBottom: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#111827', flex: 1 }}>
          {form.id ? t('vd.editItem', 'Edit item') : t('vd.newItem', 'New item')}
        </span>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer' }}>✕</button>
      </div>

      <div style={label}>{t('vd.section', 'Section')}</div>
      <input style={input} list="vd-menu-sections" value={form.section} onChange={(e) => set('section', e.target.value)} placeholder={t('vd.sectionPlaceholder', 'e.g. Mains, Drinks')} />
      <datalist id="vd-menu-sections">{(sections || []).map((s) => <option key={s} value={s} />)}</datalist>

      <div style={label}>🇻🇳 {t('vd.nameVi', 'Name (Vietnamese — shows first)')}</div>
      <input style={input} value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={160} autoFocus />

      <div style={label}>🇬🇧 {t('vd.nameEn', 'Name (English — for tourists)')}</div>
      <input style={input} value={form.name_en} onChange={(e) => set('name_en', e.target.value)} maxLength={160} />

      <div style={label}>{t('vd.priceVnd', 'Price (VND)')}</div>
      <input style={input} type="number" inputMode="numeric" value={form.price_vnd} onChange={(e) => set('price_vnd', e.target.value)} placeholder="e.g. 45000" />

      <div style={label}>{t('vd.description', 'Description (optional)')}</div>
      <input style={input} value={form.description} onChange={(e) => set('description', e.target.value)} maxLength={400} />

      <div style={label}>{t('vd.photoUrl', 'Photo URL (optional)')}</div>
      <input style={input} value={form.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="https://…" />

      <label style={{ display: 'flex', gap: 9, alignItems: 'center', fontSize: 13.5, color: '#374151', padding: '14px 2px 4px', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.is_vegetarian} onChange={(e) => set('is_vegetarian', e.target.checked)} style={{ width: 18, height: 18 }} />
        <span>🌱 {t('vd.vegetarian', 'Vegetarian')}</span>
      </label>

      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button disabled={busy} onClick={onCancel} style={{ flex: 1, background: 'white', border: '1px solid #d1d5db', color: '#374151', borderRadius: 12, padding: '12px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {t('common.cancel', 'Cancel')}
        </button>
        <button disabled={busy || !form.name.trim()} onClick={() => onSave(form)} style={{ flex: 2, background: `linear-gradient(135deg, ${ORANGE}, #c2410c)`, border: 'none', color: 'white', borderRadius: 12, padding: '12px 0', fontSize: 14, fontWeight: 800, cursor: 'pointer', opacity: (busy || !form.name.trim()) ? 0.6 : 1 }}>
          {busy ? t('common.saving', 'Saving…') : `💾 ${t('common.save', 'Save')}`}
        </button>
      </div>
    </div>
  );
}
